import React, { useState, useEffect, useContext, useCallback } from 'react';
import InventoryModal from './InventoryModal';
import AddToUpgradeModal from './AddToUpgradeModal';
import ProjectModal from './ProjectModal';
import StorageCategoryModal from './StorageCategoryModal';
import CompatibilityModal from './CompatibilityModal';
import { OverlayTrigger, Tooltip, Form, Button } from 'react-bootstrap';
import { AuthContext } from '../AuthContext';
import { hasGroup } from '../utils/permissions';
import { buildMediaUrl } from '../apiConfig';
import axiosInstance from '../axiosConfig';
import { getComponentModalData } from '../utils/componentModalDataCache';
import Select from 'react-select';
import './ComponentDetails.css';

const isIgnoredRequestError = (error) => (
  error?.code === 'ERR_CANCELED'
  || error?.code === 'ECONNABORTED'
);

/** Нормализуем поле совместимостей в массив строк для безопасного отображения */
const toCompatNames = (compat, options = []) => {
  if (!compat) return [];
  // Если уже массив
  if (Array.isArray(compat)) {
    return compat
      .map(item => {
        // Число/строка — попробуем сопоставить с options по value, иначе покажем как строку
        if (typeof item === 'string' || typeof item === 'number') {
          const opt = options.find(o => o.value === item);
          return opt ? opt.label : String(item);
        }
        // Объект — возьмём name/label, иначе попытка вывести id
        if (item && typeof item === 'object') {
          return item.name || item.label || (item.id != null ? String(item.id) : '');
        }
        return '';
      })
      .filter(Boolean);
  }
  // Одиночная строка — возможно список через запятую
  if (typeof compat === 'string') {
    return compat.split(',').map(s => s.trim()).filter(Boolean);
  }
  return [];
};

function ComponentDetails({ component, onUpdateCart }) {
  const { user, selectedServiceCenter, setComponentsUpdated, authTokens, projects } = useContext(AuthContext);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showStorageCategoryModal, setShowStorageCategoryModal] = useState(false);
  const [showCompatibilityModal, setShowCompatibilityModal] = useState(false);
  const [storages, setStorages] = useState([]);
  const [compatibilities, setCompatibilities] = useState([]);
  const [actionType, setActionType] = useState(null);
  const [componentData, setComponentData] = useState(component);
  const [quantityToAdd, setQuantityToAdd] = useState(1);
  const [quantityInCart, setQuantityInCart] = useState(0);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const isStorekeeper = hasGroup(user, 'Кладовщик');

  const [isEditing, setIsEditing] = useState({
    name: false,
    description: false,
    batch_number: false,
    storage_location: false,
    project: false,
    compatibility: false,
  });

  const [editedData, setEditedData] = useState({
    name: component.name,
    description: component.description,
    batch_number: component.batch_number,
    storage_location: component.storage_location,
    project: component.project,
    // для отправки в API удобнее хранить массив ID, но на вход может прийти что угодно — нормализуем при открытии режима редактирования
    compatibility: Array.isArray(component.compatibility)
      ? component.compatibility.map(v =>
          typeof v === 'object' ? (v.id ?? v.value ?? v.name ?? v.label) : v
        )
      : [],
  });

  const showToast = (text, type = 'info', duration = 4000) => {
    const bg = type === 'success'
      ? 'linear-gradient(to right, #00b09b, #96c93d)'
      : type === 'danger'
        ? 'linear-gradient(to right, #dc3545, #f46b6b)'
        : 'linear-gradient(to right, #1e3c72, #2a5298)';
    window.Toastify?.({
      text,
      duration,
      gravity: 'top',
      position: 'right',
      style: { background: bg },
      stopOnFocus: true,
      className: 'custom-toast',
    })?.showToast();
  };

  // Оборачивание функции с useCallback
  const fetchStoragesAndCompatibilities = useCallback(async () => {
    try {
      const data = await getComponentModalData({
        authToken: authTokens.access,
        serviceCenterId: selectedServiceCenter?.id,
      });
      setStorages((data.storages || []).map(storage => ({
        value: storage.id,
        label: storage.name
      })));
      setCompatibilities((data.compatibilities || []).map(compatibility => ({
        value: compatibility.id,
        label: compatibility.name
      })));
    } catch (error) {
      if (isIgnoredRequestError(error)) {
        return;
      }
      console.error('Ошибка при загрузке данных:', error);
    }
  }, [authTokens.access, selectedServiceCenter?.id]);

  useEffect(() => {
    setComponentData(component);
    setQuantityToAdd(1);
    const controller = new AbortController();
    fetchQuantityInCart(component.id, controller.signal);
    fetchStoragesAndCompatibilities(); // Справочники при монтировании/смене компонента
    return () => controller.abort();
  }, [component, fetchStoragesAndCompatibilities]);

  const fetchQuantityInCart = async (componentId, signal) => {
    try {
      const response = await axiosInstance.get('/api/cart/', { signal });
      if (signal?.aborted) {
        return;
      }
      const item = response.data.items?.find(item => item.component_id === componentId);
      setQuantityInCart(item ? item.quantity : 0);
    } catch (error) {
      if (isIgnoredRequestError(error)) {
        return;
      }
      console.error('Ошибка при получении данных корзины:', error);
    }
  };

  const handleOpenInventoryModal = (action) => {
    setActionType(action);
    setShowInventoryModal(true);
  };

  const handleCloseInventoryModal = () => {
    setShowInventoryModal(false);
  };

  const handleOpenUpgradeModal = () => {
    setShowUpgradeModal(true);
  };

  const handleCloseUpgradeModal = () => {
    setShowUpgradeModal(false);
  };

  const handleEditClick = (field) => {
    // при входе в режим редактирования совместимостей — подготовим editedData.compatibility как массив ID
    if (field === 'compatibility') {
      setEditedData(prev => {
        const ids = Array.isArray(componentData.compatibility)
          ? componentData.compatibility.map(v => {
              if (typeof v === 'object') return v.id ?? v.value ?? null;
              if (typeof v === 'string' || typeof v === 'number') {
                // попробуем найти по label → value, если это имена
                const opt = compatibilities.find(o => o.label === v);
                return opt ? opt.value : v;
              }
              return null;
            }).filter(v => v != null)
          : [];
        return { ...prev, compatibility: ids };
      });
    }
    setIsEditing((prev) => ({ ...prev, [field]: true }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (selectedOptions, field) => {
    if (Array.isArray(selectedOptions)) {
      setEditedData((prev) => ({ ...prev, [field]: selectedOptions.map(option => option.value) }));
    } else {
      setEditedData((prev) => ({ ...prev, [field]: selectedOptions?.value }));
    }
  };

  const handleSaveClick = (field) => {
    const dataToUpdate = { [field]: editedData[field] };

    axiosInstance.put(`/api/component/${componentData.id}/update_component/`, dataToUpdate)
      .then((response) => {
        if (field === 'storage_location') {
          const selectedStorage = storages.find(storage => storage.value === editedData[field]);
          setComponentData((prev) => ({
            ...prev,
            [field]: selectedStorage ? selectedStorage.label : editedData[field]
          }));
        } else if (field === 'project') {
          const selectedProject = projects.find(project => project.id === editedData[field]);
          setComponentData((prev) => ({
            ...prev,
            [field]: selectedProject ? selectedProject.name : editedData[field]
          }));
        } else if (field === 'compatibility') {
          const names = toCompatNames(editedData[field], compatibilities);
          setComponentData((prev) => ({
            ...prev,
            [field]: names
          }));
        } else {
          setComponentData((prev) => ({ ...prev, [field]: editedData[field] }));
        }
        setIsEditing((prev) => ({ ...prev, [field]: false }));
        setComponentsUpdated(true);
        console.log('Изменения успешно сохранены:', response.data);
      })
      .catch((error) => {
        console.error('Ошибка при сохранении данных:', error);
      });
  };

  const handleCancelClick = (field) => {
    setEditedData((prev) => ({ ...prev, [field]: componentData[field] }));
    setIsEditing((prev) => ({ ...prev, [field]: false }));
  };

  const handleCreateProject = (newProject) => {
    setEditedData(prevData => ({ ...prevData, project: newProject.id }));
    fetchStoragesAndCompatibilities(); // Обновить список проектов
  };

  const handleAddToCart = async () => {
    if (isAddingToCart) return;

    setIsAddingToCart(true);
    try {
      const response = await axiosInstance.post('/api/cart/add/', {
        component_id: componentData.id,
        quantity: quantityToAdd,
      });

      if (response?.data?.success) {
        setQuantityInCart(prev => prev + quantityToAdd);
        // Жёстко обновим детали компонента (актуальный остаток после резерва)
        await fetchComponentDetails(componentData.id);
        onUpdateCart && onUpdateCart();
        showToast('Добавлено в корзину', 'success');
        return;
      }

      // fallback
      showToast(response?.data?.error || 'Не удалось добавить в корзину', 'danger');
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;

      if (status === 409 && (data?.reason === 'out_of_stock' || data?.reason === 'insufficient_stock')) {
        const available = Number.isFinite(data?.available) ? data.available : (data?.component?.quantity ?? 0);

        // Обновляем карточку актуальными данными (и совместимости нормализуем)
        const payload = data?.component ? { ...data.component } : {};
        const compatNames = toCompatNames(payload.compatibility, compatibilities);
        setComponentData(prev => ({
          ...prev,
          ...payload,
          compatibility: compatNames,
          quantity: available
        }));

        // Подрезаем локальный запрос до доступного
        setQuantityToAdd(prev => Math.max(1, Math.min(prev, available)));

        showToast(
          available > 0 ? `Недостаточно на складе. Доступно: ${available} шт.` : 'Товар закончился на складе',
          'danger'
        );
        return;
      }

      console.error('Ошибка при добавлении в корзину:', error);
      showToast('Произошла ошибка при добавлении в корзину', 'danger');
    } finally {
      setIsAddingToCart(false);
    }
  };

  const fetchComponentDetails = async (componentId) => {
    try {
      const response = await axiosInstance.get(`/api/component/${componentId}/`, {
        params: selectedServiceCenter?.id ? { service_center: selectedServiceCenter.id } : {}
      });
      // нормализуем совместимости для показа
      const compatNames = toCompatNames(response.data.compatibility, compatibilities);
      setComponentData({ ...response.data, compatibility: compatNames });
    } catch (error) {
      console.error('Ошибка при загрузке деталей компонента:', error);
    }
  };

  // Безопасный вывод совместимостей
  const compatibilityText = (() => {
    const names = toCompatNames(componentData.compatibility, compatibilities);
    return names.length ? names.join(', ') : 'Не указано';
  })();

  const detailSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '46px',
      borderRadius: '14px',
      borderColor: state.isFocused ? 'rgba(15, 118, 110, 0.22)' : 'rgba(148, 163, 184, 0.2)',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      boxShadow: state.isFocused
        ? '0 0 0 4px rgba(20, 184, 166, 0.12)'
        : 'inset 0 1px 0 rgba(255, 255, 255, 0.78), 0 4px 14px rgba(15, 23, 42, 0.04)',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 30,
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 18px 36px rgba(15, 23, 42, 0.12)',
    }),
    valueContainer: (provided) => ({
      ...provided,
      minHeight: '44px',
      padding: '0 14px',
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
      color: '#334155',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#64748b',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#334155',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: 'rgba(100, 116, 139, 0.82)',
      padding: '0 14px',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? 'rgba(204, 251, 241, 0.58)' : '#fff',
      color: '#0f172a',
    }),
  };

  return (
    <div className="container mt-4 componentDetails">
      <div className="row">
        <div className="col-md-6 text-center">
          {componentData.photo && (
            <div className="image-slider position-relative">
              <img
                src={buildMediaUrl(componentData.photo)}
                alt={componentData.name}
                className="component-image img-fluid"
              />
              {componentData.quantity === 0 && (
                <div className="watermark">Отсутствует</div>
              )}
            </div>
          )}
        </div>
        <div className="col-md-6">
          {/* Название */}
          <h1>
            {isEditing.name ? (
              <>
                <Form.Control
                  type="text"
                  name="name"
                  value={editedData.name}
                  onChange={handleInputChange}
                />
                <div className="mt-2">
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-save-name">Сохранить</Tooltip>}
                  >
                    <Button variant="success" onClick={() => handleSaveClick('name')} className="mr-2">
                      <i className="fas fa-check"></i>
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-cancel-name">Отмена</Tooltip>}
                  >
                    <Button variant="secondary" onClick={() => handleCancelClick('name')} className="mr-2">
                      <i className="fas fa-times"></i>
                    </Button>
                  </OverlayTrigger>
                </div>
              </>
            ) : (
              <span className="editable-field">
                {componentData.name}
                {isStorekeeper && (
                  <i className="fas fa-pencil-alt edit-icon" onClick={() => handleEditClick('name')}></i>
                )}
              </span>
            )}
          </h1>

          {/* Описание */}
          <p className="editable-field">
            {isEditing.description ? (
              <>
                <Form.Control
                  as="textarea"
                  name="description"
                  value={editedData.description}
                  onChange={handleInputChange}
                />
                <div className="mt-2">
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-save-description">Сохранить</Tooltip>}
                  >
                    <Button variant="success" onClick={() => handleSaveClick('description')} className="mr-2">
                      <i className="fas fa-check"></i>
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-cancel-description">Отмена</Tooltip>}
                  >
                    <Button variant="secondary" onClick={() => handleCancelClick('description')} className="mr-2">
                      <i className="fas fa-times"></i>
                    </Button>
                  </OverlayTrigger>
                </div>
              </>
            ) : (
              <>
                {componentData.description}
                {isStorekeeper && (
                  <i className="fas fa-pencil-alt edit-icon" onClick={() => handleEditClick('description')}></i>
                )}
              </>
            )}
          </p>

          {componentData.upgrade && (
            <>
              <p>В апгрейде: {componentData.upgrade}</p>
              <p>Количество в апгрейде: {componentData.quantity_in_upgrade}</p>
            </>
          )}
          <p>Количество на складе: {componentData.quantity}</p>

          {/* Партийный номер */}
          <p className="editable-field">
            Партийный номер:&nbsp;
            {isEditing.batch_number ? (
              <>
                <Form.Control
                  type="text"
                  name="batch_number"
                  value={editedData.batch_number}
                  onChange={handleInputChange}
                />
                <div className="mt-2">
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-save-batch_number">Сохранить</Tooltip>}
                  >
                    <Button variant="success" onClick={() => handleSaveClick('batch_number')} className="mr-2">
                      <i className="fas fa-check"></i>
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-cancel-batch_number">Отмена</Tooltip>}
                  >
                    <Button variant="secondary" onClick={() => handleCancelClick('batch_number')} className="mr-2">
                      <i className="fas fa-times"></i>
                    </Button>
                  </OverlayTrigger>
                </div>
              </>
            ) : (
              <>
                {componentData.batch_number}
                {isStorekeeper && (
                  <i className="fas fa-pencil-alt edit-icon" onClick={() => handleEditClick('batch_number')}></i>
                )}
              </>
            )}
          </p>

          {/* Место хранения */}
          <p className="editable-field">
            Место хранения:&nbsp;
            {isEditing.storage_location ? (
              <>
                <Select
                  name="storage_location"
                  options={storages}
                  value={storages.find(storage => storage.value === editedData.storage_location)}
                  onChange={(option) => handleSelectChange(option, 'storage_location')}
                  styles={detailSelectStyles}
                  classNamePrefix="warehouse-select"
                  isSearchable
                  isClearable
                />
                <div className="mt-2">
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-save-storage_location">Сохранить</Tooltip>}
                  >
                    <Button variant="success" onClick={() => handleSaveClick('storage_location')} className="mr-2">
                      <i className="fas fa-check"></i>
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-cancel-storage_location">Отмена</Tooltip>}
                  >
                    <Button variant="secondary" onClick={() => handleCancelClick('storage_location')} className="mr-2">
                      <i className="fas fa-times"></i>
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-create-storage_location">Создать место хранения</Tooltip>}
                  >
                    <Button variant="primary" onClick={() => setShowStorageCategoryModal(true)}>
                      <i className="fas fa-plus"></i>
                    </Button>
                  </OverlayTrigger>
                </div>
              </>
            ) : (
              <>
                {componentData.storage_location}
                {isStorekeeper && (
                  <i className="fas fa-pencil-alt edit-icon" onClick={() => handleEditClick('storage_location')}></i>
                )}
              </>
            )}
          </p>

          {/* Проект */}
          <p className="editable-field">
            Проект:&nbsp;
            {isEditing.project ? (
              <>
                <Select
                  name="project"
                  options={projects.map(p => ({ value: p.id, label: p.name }))}
                  value={projects.map(p => ({ value: p.id, label: p.name })).find(project => project.value === editedData.project)}
                  onChange={(option) => handleSelectChange(option, 'project')}
                  styles={detailSelectStyles}
                  classNamePrefix="warehouse-select"
                  isSearchable
                />
                <div className="mt-2">
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-save-project">Сохранить</Tooltip>}
                  >
                    <Button variant="success" onClick={() => handleSaveClick('project')} className="mr-2">
                      <i className="fas fa-check"></i>
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-cancel-project">Отмена</Tooltip>}
                  >
                    <Button variant="secondary" onClick={() => handleCancelClick('project')} className="mr-2">
                      <i className="fas fa-times"></i>
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-create-project">Создать проект</Tooltip>}
                  >
                    <Button variant="primary" onClick={() => setShowProjectModal(true)}>
                      <i className="fas fa-plus"></i>
                    </Button>
                  </OverlayTrigger>
                </div>
              </>
            ) : (
              <>
                {componentData.project}
                {isStorekeeper && (
                  <i className="fas fa-pencil-alt edit-icon" onClick={() => handleEditClick('project')}></i>
                )}
              </>
            )}
          </p>

          {/* Совместимость */}
          <p className="editable-field">
            Совместимость:&nbsp;
            {isEditing.compatibility ? (
              <>
                <Select
                  name="compatibility"
                  options={compatibilities}
                  value={compatibilities.filter(comp => editedData.compatibility.includes(comp.value))}
                  onChange={(options) => handleSelectChange(options, 'compatibility')}
                  isMulti
                  isClearable
                  styles={detailSelectStyles}
                  classNamePrefix="warehouse-select"
                  isSearchable
                />
                <div className="mt-2">
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-save-compatibility">Сохранить</Tooltip>}
                  >
                    <Button variant="success" onClick={() => handleSaveClick('compatibility')} className="mr-2">
                      <i className="fas fa-check"></i>
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-cancel-compatibility">Отмена</Tooltip>}
                  >
                    <Button variant="secondary" onClick={() => handleCancelClick('compatibility')} className="mr-2">
                      <i className="fas fa-times"></i>
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-create-compatibility">Создать совместимость</Tooltip>}
                  >
                    <Button variant="primary" onClick={() => setShowCompatibilityModal(true)}>
                      <i className="fas fa-plus"></i>
                    </Button>
                  </OverlayTrigger>
                </div>
              </>
            ) : (
              <>
                {compatibilityText}
                {isStorekeeper && (
                  <i className="fas fa-pencil-alt edit-icon" onClick={() => handleEditClick('compatibility')}></i>
                )}
              </>
            )}
          </p>

          {/* Количество для добавления (если нужно менять) */}
          {/* Если у вас в дизайне нет инпута, можно скрыть. Оставляю пример: */}
          {/* <div className="d-flex align-items-center mb-2">
            <input
              type="number"
              min="1"
              value={quantityToAdd}
              onChange={e => setQuantityToAdd(Math.max(1, parseInt(e.target.value || '1', 10)))}
              className="form-control"
              style={{ maxWidth: 120 }}
            />
          </div> */}

          {/* Кнопки действия */}
          <div className="action-buttons mt-3">
            {!isStorekeeper && componentData.quantity > 0 && (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="tooltip-add-to-cart">Добавить в заказ</Tooltip>}
              >
                {quantityToAdd <= componentData.quantity ? (
                  <button className="btn btn-primary" onClick={handleAddToCart} disabled={isAddingToCart}>
                    <i className="fas fa-cart-plus"></i>
                  </button>
                ) : (
                  <button className="btn btn-primary" disabled>
                    <i className="fas fa-cart-plus"></i>
                  </button>
                )}
              </OverlayTrigger>
            )}

            {isStorekeeper && (
              <>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="tooltip-in">Оприходовать</Tooltip>}
                >
                  <button className="btn btn-success" onClick={() => handleOpenInventoryModal('IN')}>
                    <i className="fas fa-plus-circle"></i>
                  </button>
                </OverlayTrigger>

                {/* Выдача со склада скрыта (как в вашем коде) */}

                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="tooltip-return">Принять возврат</Tooltip>}
                >
                  <button className="btn btn-warning" onClick={() => handleOpenInventoryModal('RETURN')}>
                    <i className="fas fa-undo-alt"></i>
                  </button>
                </OverlayTrigger>

                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="tooltip-upgrade">Добавить к апгрейду</Tooltip>}
                >
                  <button className="btn btn-primary" onClick={handleOpenUpgradeModal}>
                    <i className="fas fa-box"></i>
                  </button>
                </OverlayTrigger>
              </>
            )}
          </div>
        </div>
      </div>

      <InventoryModal
        show={showInventoryModal}
        handleClose={handleCloseInventoryModal}
        componentId={componentData.id}
        actionType={actionType}
        serviceCenterId={selectedServiceCenter.id}
        onUpdateComponent={() => fetchComponentDetails(componentData.id)}
      />

      <AddToUpgradeModal
        show={showUpgradeModal}
        handleClose={handleCloseUpgradeModal}
        componentId={componentData.id}
        onUpdateUpgrade={() => fetchComponentDetails(componentData.id)} 
      />

      <ProjectModal
        show={showProjectModal}
        handleClose={() => setShowProjectModal(false)}
        onSubmit={handleCreateProject}
      />

      <StorageCategoryModal
        show={showStorageCategoryModal}
        handleClose={() => setShowStorageCategoryModal(false)}
        onSubmit={fetchStoragesAndCompatibilities}
      />

      <CompatibilityModal
        show={showCompatibilityModal}
        handleClose={() => setShowCompatibilityModal(false)}
        onSubmit={fetchStoragesAndCompatibilities}
      />
    </div>
  );
}

export default ComponentDetails;
